import { Component, ErrorInfo, ReactNode } from "react"
import { useTranslation } from "react-i18next"
import * as Sentry from "@sentry/react"
import { RiErrorWarningLine } from "@remixicon/react"

import { usePdfBuilderStore } from "@/stores/pdfBuilder"

interface Props {
  children: ReactNode
  /** 어떤 아이템인지 (Sentry 태그·다시 그리기 판단용) */
  itemId: string
  chartType: string
  title?: string
  /** 값이 바뀌면 오류 상태를 풀고 다시 그린다 (예: 아이템 설정이 바뀔 때) */
  resetKey?: string
}

interface State {
  error: Error | null
}

function CanvasItemErrorFallback({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const { t } = useTranslation()
  const isExporting = usePdfBuilderStore((s) => s.isExporting)

  // 내보내는 중에는 안내 대신 빈 칸 — 실패한 아이템이 PDF 에 안내 문구로 찍히지 않게
  if (isExporting) {
    return <div className="CanvasItemError CanvasItemError--exporting" />
  }

  return (
    <div className="CanvasItemError" role="alert">
      <RiErrorWarningLine size={20} />
      <p className="CanvasItemError__message">
        {t("componentRenderError", "This component could not be drawn")}
      </p>
      <p className="CanvasItemError__detail" title={error.message}>
        {error.message}
      </p>
      <button type="button" className="CanvasItemError__retry" onClick={onRetry}>
        {t("retry", "Retry")}
      </button>
    </div>
  )
}

/**
 * 캔버스 아이템 하나를 격리하는 ErrorBoundary (2026-09-28, stage React #185 이후).
 * 아이템 하나가 그리다 터져도 그 칸만 안내로 바뀌고 나머지 편집 화면·저장·필터는 계속 동작한다.
 * 무한 갱신처럼 렌더 중 반복되는 오류도 이 경계가 하위 트리를 내리면서 멈춘다. 오류는 Sentry 로 보낸다.
 */
export default class CanvasItemErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, {
      tags: { source: "pdf-builder-item", chartType: this.props.chartType },
      extra: {
        itemId: this.props.itemId,
        title: this.props.title,
        componentStack: info.componentStack,
      },
    })
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  handleRetry = () => {
    this.setState({ error: null })
  }

  render() {
    if (this.state.error) {
      return <CanvasItemErrorFallback error={this.state.error} onRetry={this.handleRetry} />
    }

    return this.props.children
  }
}
